/**
 * Portfolios Tab - Manage client portfolios
 * TASK-210: Portfolio Management Module - Frontend Prototype
 * TASK-263: Updated to use real backend API for multi-portfolio support
 *
 * Shows list of portfolios for a client with ability to create new ones
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusCircle,
  Briefcase,
  TrendingUp,
  Filter,
  Search
} from 'lucide-react';
import PortfolioCard from '../../portfolio/PortfolioCard';
import PortfolioCreationWizard from '../../portfolio/PortfolioCreationWizard';
import ManualAssetEntryForm from '../../portfolio/ManualAssetEntryForm';
import PortfolioFileUploader from '../../portfolio/PortfolioFileUploader';
import EditPortfolioModal from '../../portfolio/EditPortfolioModal';
import ClosePortfolioDialog from '../../portfolio/ClosePortfolioDialog';
// TASK-263: Import real API service
import {
  portfolioApiService,
  Portfolio
} from '../../../services/api/portfolioApiService';
// TASK-277: MockPortfolio types for component compatibility (wizard passes this type)
import type { MockPortfolio, MockManualAsset } from '../../../services/mock/portfolioMockData';
import { toast } from 'sonner';
import {
  usePortfolioStore,
  useNavigationActions,
  usePortfolioSelectionActions
} from '../../../services/portfolio-refactor/store/portfolioStore';

interface PortfoliosTabProps {
  clientId: string;
  // Optional: as a canvas widget (TASK-494) only clientId is server-injected.
  // Used only for cosmetic copy + forwarded to PortfolioCreationWizard.
  clientName?: string;
}

/**
 * TASK-263: Extended portfolio type that combines API data with display fields
 * Used for UI rendering - maps API Portfolio to expected display format
 * Compatible with PortfolioCard component (which expects MockPortfolio-like structure)
 */
interface EnrichedPortfolio extends Portfolio {
  // Display properties (mapped from API)
  portfolioName: string;  // Maps from name
  portfolioType: 'manual' | 'api';  // Derived from portfolio_type
  status: 'active' | 'closed';  // Maps from is_active
  // Financial data (fetched from overview API)
  totalValue: number;
  totalSecurities: number;
  totalCash: number;
  totalAlternativeAssets: number;
  numberOfPositions: number;
  // API connection (for sync UI)
  apiConnectionStatus?:
    | 'connected'
    | 'disconnected'
    | 'syncing'
    | 'error'
    | 'never_synced'
    | 'partial'
    | 'failed'
    | 'pending'
    | 'running';
  capabilityContract?: Portfolio['capability_contract'];
  syncEnabled?: boolean;
  lastSyncAt?: string;
  lastSyncDate?: string;  // Alias for PortfolioCard compatibility
  lastSyncStatus?: 'success' | 'partial' | 'failed' | 'pending';
  // TASK-321: Data source configuration
  dataSource?: 'manual' | 'pretim' | 'csv_import';
  externalId?: string;  // Pretim portfolio ID
  syncFrequency?: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
  // Optional fields for UI (PortfolioCard compatibility)
  accountNumber?: string;
  currencyBase?: string;  // Alias for currency
  manualAssets?: MockManualAsset[];  // Use MockManualAsset type directly
}

const PortfoliosTab: React.FC<PortfoliosTabProps> = ({ clientId, clientName = 'this client' }) => {
  // ponytail: route-navigates to portfolio detail; on the canvas (TASK-494) a
  // click leaves the grid. Phase-2 (TASK-500): swap navigate() for
  // open_view()/addSpecs to summon the portfolio as a tile instead.
  const navigate = useNavigate();
  const { setSelectedPortfolio } = usePortfolioStore();
  const { drillDown } = useNavigationActions(); // TASK-210: Portfolio navigation
  const { setClientPortfolios } = usePortfolioSelectionActions(); // TASK-263: Store portfolios in global state

  // TASK-263: Updated to use EnrichedPortfolio type
  const [portfolios, setPortfolios] = useState<EnrichedPortfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [_apiError, setApiError] = useState<string | null>(null);  // Prefixed to silence TS warning (displayed in console)
  const [filterType, setFilterType] = useState<'all' | 'manual' | 'api'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'closed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [selectedPortfolioForAsset, setSelectedPortfolioForAsset] = useState<EnrichedPortfolio | null>(null);
  const [selectedPortfolioForUpload, setSelectedPortfolioForUpload] = useState<EnrichedPortfolio | null>(null);
  const [selectedPortfolioForEdit, setSelectedPortfolioForEdit] = useState<EnrichedPortfolio | null>(null);
  const [selectedPortfolioForClose, setSelectedPortfolioForClose] = useState<EnrichedPortfolio | null>(null);

  // Load portfolios on mount
  useEffect(() => {
    loadPortfolios();
  }, [clientId]);

  /**
   * TASK-263: Map API Portfolio to EnrichedPortfolio for UI
   */
  const mapToEnrichedPortfolio = (
    portfolio: Portfolio,
    financialData?: { totalValue?: number; positions?: number; cash?: number }
  ): EnrichedPortfolio => {
    // TASK-657: the SERVER derives ownership/connection via capability_contract;
    // the UI never fabricates "connected" from the mere presence of a source.
    const contract = portfolio.capability_contract;
    const isConnectedMode = contract
      ? contract.mode === 'connected'
      : Boolean(portfolio.data_source && portfolio.data_source !== 'manual');

    return {
      ...portfolio,
      // Map display properties
      portfolioName: portfolio.name,
      portfolioType: isConnectedMode ? 'api' : 'manual',
      status: portfolio.is_active ? 'active' : 'closed',
      // Financial data (default to 0 if not available)
      totalValue: financialData?.totalValue || 0,
      totalSecurities: financialData?.totalValue || 0,  // Will be refined when we fetch positions
      totalCash: financialData?.cash || 0,
      totalAlternativeAssets: 0,
      numberOfPositions: financialData?.positions || 0,
      // TASK-657: connection state comes from the REAL sync lifecycle — a
      // never-synced external portfolio is 'never_synced', not 'connected'.
      apiConnectionStatus: isConnectedMode
        ? contract?.connection_status === 'success'
          ? 'connected'
          : (contract?.connection_status as EnrichedPortfolio['apiConnectionStatus']) ?? 'never_synced'
        : undefined,
      capabilityContract: contract,
      syncEnabled: portfolio.sync_enabled ?? isConnectedMode,
      lastSyncAt: portfolio.last_sync_at || portfolio.updated_at,
      lastSyncDate: portfolio.last_sync_at || portfolio.updated_at,  // PortfolioCard compatibility
      lastSyncStatus: portfolio.last_sync_status,
      // TASK-321: Sync configuration
      dataSource: portfolio.data_source || 'manual',
      externalId: portfolio.external_portfolio_id || portfolio.external_id,
      syncFrequency: portfolio.sync_frequency,
      // PortfolioCard compatibility fields
      currencyBase: portfolio.currency,
      manualAssets: [],  // Will be populated when we fetch positions
    };
  };

  /**
   * TASK-263: Load portfolios from real API with fallback to mock data
   */
  const loadPortfolios = async () => {
    setLoading(true);
    setApiError(null);

    try {
      // Fetch portfolios from real API
      const response = await portfolioApiService.getClientPortfolios(clientId, true); // Include inactive
      const apiPortfolios = response.portfolios;

      // Fetch financial data for each portfolio in parallel
      const enrichedPortfolios = await Promise.all(
        apiPortfolios.map(async (portfolio) => {
          try {
            // Fetch overview and positions count in parallel
            const [overview, positionsResponse] = await Promise.all([
              portfolioApiService.getPortfolioOverview(clientId, portfolio.id),
              portfolioApiService.getPositions(clientId, portfolio.id),
            ]);
            return mapToEnrichedPortfolio(portfolio, {
              // Use snake_case fields from PortfolioOverview API response
              totalValue: overview.total_value,
              positions: positionsResponse.count || positionsResponse.positions?.length || 0,
              cash: overview.total_cash || 0,
            });
          } catch {
            // If fetch fails, return with default financial data
            return mapToEnrichedPortfolio(portfolio);
          }
        })
      );

      setPortfolios(enrichedPortfolios);
      // TASK-263: Store portfolios in global state for other components
      setClientPortfolios(apiPortfolios);
    } catch (err) {
      console.error('Failed to fetch portfolios from API:', err);
      setApiError('Could not load portfolios. Please check your connection and try again.');
      setPortfolios([]);
      toast.error('Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  // Filter portfolios
  const filteredPortfolios = portfolios.filter(portfolio => {
    // Type filter
    if (filterType !== 'all' && portfolio.portfolioType !== filterType) {
      return false;
    }

    // Status filter
    if (filterStatus !== 'all' && portfolio.status !== filterStatus) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        portfolio.portfolioName.toLowerCase().includes(query) ||
        portfolio.custodian?.toLowerCase().includes(query) ||
        portfolio.accountNumber?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // TASK-263: Calculate stats from loaded portfolios (not mock function)
  const stats = {
    totalValue: portfolios
      .filter(p => p.status === 'active')
      .reduce((sum, p) => sum + p.totalValue, 0),
    totalPortfolios: portfolios.filter(p => p.status === 'active').length,
    apiPortfolios: portfolios.filter(p => p.portfolioType === 'api' && p.status === 'active').length,
    manualPortfolios: portfolios.filter(p => p.portfolioType === 'manual' && p.status === 'active').length
  };

  // Format currency
  const formatCurrency = (value: number, currency: string = 'CHF') => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Handle portfolio view - Navigate to Portfolio Dashboard
  // TASK-210: Pass both clientId and portfolioId in single call
  const handleView = (portfolioId: string) => {
    // Find the portfolio to get its name
    const portfolio = portfolios.find(p => p.id === portfolioId);

    if (!portfolio) {
      toast.error('Portfolio not found');
      return;
    }

    // Navigate to portfolio level with both clientId and portfolioId
    drillDown('portfolio', portfolioId, clientId);

    // Set the selected portfolio in portfolioStore
    setSelectedPortfolio(portfolioId);

    // TASK-371: Navigate to URL-based portfolio route
    navigate(`/portfolio/client/${clientId}/portfolio/${portfolioId}`);

    // Show success toast with portfolio name
    toast.info(`Viewing ${portfolio.portfolioName}`);
  };

  /**
   * TASK-277: Handle portfolio sync using real Pretim API
   */
  const handleSync = async (portfolioId: string) => {
    // Find the portfolio to get client_id
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (!portfolio) {
      toast.error('Portfolio not found');
      return;
    }

    // Update portfolio status to syncing
    setPortfolios(prev => prev.map(p =>
      p.id === portfolioId
        ? { ...p, apiConnectionStatus: 'syncing' as const }
        : p
    ));

    try {
      // Call real Pretim sync API
      const syncResult = await portfolioApiService.syncPretim(clientId, portfolioId);

      // Fetch updated portfolio data after sync
      const overview = await portfolioApiService.getPortfolioOverview(clientId, portfolioId);

      // Update portfolio with synced data
      setPortfolios(prev => prev.map(p => {
        if (p.id === portfolioId) {
          return {
            ...p,
            totalValue: overview.total_value || 0,
            totalSecurities: overview.total_value || 0,
            totalCash: overview.total_cash || 0,
            numberOfPositions: syncResult.synced.positions_synced,
            apiConnectionStatus: 'connected' as const,
            lastSyncDate: syncResult.synced.last_sync,
            lastSyncAt: syncResult.synced.last_sync,
          };
        }
        return p;
      }));

      toast.success(`Synced ${syncResult.synced.positions_synced} positions from Pretim`);
    } catch (err) {
      console.error('Failed to sync portfolio:', err);
      toast.error('Failed to sync portfolio');

      // Revert status on error
      setPortfolios(prev => prev.map(p =>
        p.id === portfolioId
          ? { ...p, apiConnectionStatus: 'error' as const }
          : p
      ));
    }
  };

  // Handle portfolio edit
  const handleEdit = (portfolioId: string) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (portfolio) {
      setSelectedPortfolioForEdit(portfolio);
      setShowEditModal(true);
    }
  };

  /**
   * TASK-263: Handle save edited portfolio - update via API
   * TASK-321: Refetch portfolios after save to get updated sync fields
   */
  const handleEditSave = async (updatedFields: Partial<MockPortfolio>) => {
    if (!selectedPortfolioForEdit) return;

    try {
      // Map to API update format (MockPortfolio uses currencyBase, not currency)
      const updateData: Partial<Portfolio> = {};
      if (updatedFields.portfolioName) updateData.name = updatedFields.portfolioName;
      if (updatedFields.custodian) updateData.custodian = updatedFields.custodian;
      if (updatedFields.currencyBase) updateData.currency = updatedFields.currencyBase;

      // Only call updatePortfolio if there are basic field updates
      if (Object.keys(updateData).length > 0) {
        await portfolioApiService.updatePortfolio(selectedPortfolioForEdit.id, updateData);
      }

      // TASK-321: Refetch portfolios to get updated sync configuration
      // This ensures sync fields updated via updateSyncConfig are reflected
      await loadPortfolios();

      toast.success('Portfolio updated successfully!');
    } catch (err) {
      console.error('Failed to update portfolio via API:', err);
      // Fallback: Update locally - only copy compatible fields
      const updatedPortfolio: EnrichedPortfolio = {
        ...selectedPortfolioForEdit,
        portfolioName: updatedFields.portfolioName || selectedPortfolioForEdit.portfolioName,
        custodian: updatedFields.custodian || selectedPortfolioForEdit.custodian,
        currencyBase: updatedFields.currencyBase || selectedPortfolioForEdit.currencyBase,
        currency: updatedFields.currencyBase || selectedPortfolioForEdit.currency,
      };
      setPortfolios(prev => prev.map(p =>
        p.id === updatedPortfolio.id ? updatedPortfolio : p
      ));
      toast.info('Portfolio updated locally (offline mode)');
    }

    setShowEditModal(false);
    setSelectedPortfolioForEdit(null);
  };

  // Handle close portfolio
  const handleClosePortfolio = (portfolioId: string) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (portfolio) {
      setSelectedPortfolioForClose(portfolio);
      setShowCloseDialog(true);
    }
  };

  /**
   * TASK-263: Handle confirm close portfolio - deactivate via API
   * Note: reason and notes are passed by ClosePortfolioDialog but not used in API yet
   */
  const handleConfirmClose = async (_reason: string, _notes: string) => {
    if (!selectedPortfolioForClose) return;

    try {
      // Deactivate portfolio via API (soft delete)
      await portfolioApiService.deletePortfolio(selectedPortfolioForClose.id);

      // Update local state
      const updatedPortfolio: EnrichedPortfolio = {
        ...selectedPortfolioForClose,
        status: 'closed',
        is_active: false,
        syncEnabled: false,
        updated_at: new Date().toISOString(),
      };

      setPortfolios(prev => prev.map(p =>
        p.id === updatedPortfolio.id ? updatedPortfolio : p
      ));

      toast.success('Portfolio closed successfully');
    } catch (apiError) {
      console.error('Failed to close portfolio via API:', apiError);
      // Fallback: Update locally
      const updatedPortfolio: EnrichedPortfolio = {
        ...selectedPortfolioForClose,
        status: 'closed',
        is_active: false,
        syncEnabled: false,
        updated_at: new Date().toISOString(),
      };
      setPortfolios(prev => prev.map(p =>
        p.id === updatedPortfolio.id ? updatedPortfolio : p
      ));
      toast.info('Portfolio closed locally (offline mode)');
    }

    setShowCloseDialog(false);
    setSelectedPortfolioForClose(null);
  };

  // Handle create portfolio
  const handleCreate = () => {
    setShowWizard(true);
  };

  /**
   * TASK-263: Handle wizard success - portfolio already created via API by wizard
   * Accepts CreatedPortfolio from wizard and updates local state
   */
  const handleWizardSuccess = (createdPortfolio: { portfolio_id: string; client_id: string; name: string; status: string; dataSource?: string; syncResult?: { positions_synced: number; cash_synced: number; transactions_synced: number; } }) => {
    // Map CreatedPortfolio response to EnrichedPortfolio
    const enrichedPortfolio: EnrichedPortfolio = {
      id: createdPortfolio.portfolio_id,
      account_id: createdPortfolio.client_id || clientId,
      client_id: clientId,
      name: createdPortfolio.name,
      portfolio_type: 'investment',
      custodian: undefined,
      currency: 'CHF',
      is_active: createdPortfolio.status === 'active',
      is_default: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // Display fields
      portfolioName: createdPortfolio.name,
      portfolioType: createdPortfolio.dataSource === 'pretim' ? 'api' : 'manual',
      status: 'active',
      totalValue: 0,
      totalSecurities: 0,
      totalCash: 0,
      totalAlternativeAssets: 0,
      numberOfPositions: createdPortfolio.syncResult?.positions_synced || 0,
      apiConnectionStatus: createdPortfolio.dataSource ? 'connected' : undefined,
      syncEnabled: !!createdPortfolio.dataSource,
      currencyBase: 'CHF',
    };

    setPortfolios(prev => [...prev, enrichedPortfolio]);
    toast.success('Portfolio created successfully!');
    setShowWizard(false);
  };

  // Handle add asset
  const handleAddAsset = (portfolioId: string) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (portfolio) {
      setSelectedPortfolioForAsset(portfolio);
      setShowAssetForm(true);
    }
  };

  /**
   * TASK-277: Handle save asset - add manual asset to portfolio
   * TODO: Implement real API endpoint for manual asset creation
   */
  const handleSaveAsset = async (asset: Omit<MockManualAsset, 'id'>) => {
    if (!selectedPortfolioForAsset) return;

    try {
      // Calculate new totals locally (API integration pending)
      const newAsset: MockManualAsset = {
        ...asset,
        id: `manual-${Date.now()}`,
      };

      const currentAssets = selectedPortfolioForAsset.manualAssets || [];
      const updatedAssets = [...currentAssets, newAsset];
      const totalAlternativeAssets = updatedAssets.reduce((sum, a) => sum + a.currentValuation, 0);

      // Update portfolio in local state
      setPortfolios(prev => prev.map(p => {
        if (p.id === selectedPortfolioForAsset.id) {
          const newTotalValue = p.totalSecurities + p.totalCash + totalAlternativeAssets;
          return {
            ...p,
            totalAlternativeAssets,
            totalValue: newTotalValue,
            manualAssets: updatedAssets,
          };
        }
        return p;
      }));

      toast.success('Asset added successfully');
    } catch (err) {
      console.error('Failed to add asset:', err);
      toast.error('Failed to add asset');
    }

    setShowAssetForm(false);
    setSelectedPortfolioForAsset(null);
  };

  // Handle upload file
  const handleUploadFile = (portfolioId: string) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (portfolio) {
      setSelectedPortfolioForUpload(portfolio);
      setShowFileUploader(true);
    }
  };

  // Handle file upload success
  const handleFileUploadSuccess = (positions: Array<{ marketValue: number }>, cashPositions?: Array<{ amount: number }>) => {
    if (!selectedPortfolioForUpload) return;

    // Calculate new financial values
    const totalSecurities = positions.reduce((sum, p) => sum + p.marketValue, 0);
    const totalCash = (cashPositions || []).reduce((sum, c) => sum + c.amount, 0);
    const totalValue = totalSecurities + totalCash + selectedPortfolioForUpload.totalAlternativeAssets;

    // Update portfolio with new financial data (EnrichedPortfolio doesn't store raw positions)
    setPortfolios(prev => prev.map(p => {
      if (p.id === selectedPortfolioForUpload.id) {
        return {
          ...p,
          numberOfPositions: positions.length,
          totalSecurities,
          totalCash,
          totalValue,
          updated_at: new Date().toISOString(),
        };
      }
      return p;
    }));

    setShowFileUploader(false);
    setSelectedPortfolioForUpload(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading portfolios...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (portfolios.length === 0) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Portfolios Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Create portfolios to track {clientName}'s holdings across multiple accounts and asset types.
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <PlusCircle className="w-5 h-5" />
              Create Kielis-managed Portfolio
            </button>
          </div>
        </div>

        {/* Portfolio Creation Wizard - Must be rendered even in empty state */}
        <PortfolioCreationWizard
          clientId={clientId}
          clientName={clientName}
          isOpen={showWizard}
          onClose={() => setShowWizard(false)}
          onSuccess={handleWizardSuccess}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
          </div>
          <div className="text-sm text-blue-700 mb-1">Total AuM</div>
          <div className="text-2xl font-bold text-blue-900">
            {formatCurrency(stats.totalValue)}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-gray-600" />
            </div>
          </div>
          <div className="text-sm text-gray-600 mb-1">Total Portfolios</div>
          <div className="text-2xl font-bold text-gray-900">{stats.totalPortfolios}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <span className="text-xl">🔌</span>
            </div>
          </div>
          <div className="text-sm text-gray-600 mb-1">Connected PMS</div>
          <div className="text-2xl font-bold text-gray-900">{stats.apiPortfolios}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-xl">📊</span>
            </div>
          </div>
          <div className="text-sm text-gray-600 mb-1">Kielis-managed</div>
          <div className="text-2xl font-bold text-gray-900">{stats.manualPortfolios}</div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search portfolios..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            aria-label="Filter by portfolio mode"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="manual">Kielis-managed</option>
            <option value="api">Connected PMS</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            aria-label="Filter by status"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
        >
          <PlusCircle className="w-4 h-4" />
          Create Kielis-managed Portfolio
        </button>
      </div>

      {/* Portfolio Grid */}
      {filteredPortfolios.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Filter className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-gray-600">No portfolios match your filters</p>
          <button
            onClick={() => {
              setFilterType('all');
              setFilterStatus('all');
              setSearchQuery('');
            }}
            className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPortfolios.map(portfolio => (
            <PortfolioCard
              key={portfolio.id}
              portfolio={portfolio}
              onView={handleView}
              onSync={handleSync}
              onEdit={handleEdit}
              onAddAsset={handleAddAsset}
              onUploadFile={handleUploadFile}
              onClosePortfolio={handleClosePortfolio}
            />
          ))}
        </div>
      )}

      {/* Portfolio Creation Wizard */}
      <PortfolioCreationWizard
        clientId={clientId}
        clientName={clientName}
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={handleWizardSuccess}
      />

      {/* Manual Asset Entry Form */}
      {selectedPortfolioForAsset && (
        <ManualAssetEntryForm
          isOpen={showAssetForm}
          portfolioId={selectedPortfolioForAsset.id}
          portfolioName={selectedPortfolioForAsset.portfolioName}
          onClose={() => {
            setShowAssetForm(false);
            setSelectedPortfolioForAsset(null);
          }}
          onSave={handleSaveAsset}
        />
      )}

      {/* Portfolio File Uploader */}
      {selectedPortfolioForUpload && (
        <PortfolioFileUploader
          isOpen={showFileUploader}
          portfolioId={selectedPortfolioForUpload.id}
          portfolioName={selectedPortfolioForUpload.portfolioName}
          clientId={clientId}
          onClose={() => {
            setShowFileUploader(false);
            setSelectedPortfolioForUpload(null);
          }}
          onSuccess={handleFileUploadSuccess}
        />
      )}

      {/* Edit Portfolio Modal */}
      {selectedPortfolioForEdit && (
        <EditPortfolioModal
          isOpen={showEditModal}
          portfolio={selectedPortfolioForEdit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPortfolioForEdit(null);
          }}
          onSave={handleEditSave}
        />
      )}

      {/* Close Portfolio Dialog */}
      {selectedPortfolioForClose && (
        <ClosePortfolioDialog
          isOpen={showCloseDialog}
          portfolioName={selectedPortfolioForClose.portfolioName}
          portfolioType={selectedPortfolioForClose.portfolioType}
          onClose={() => {
            setShowCloseDialog(false);
            setSelectedPortfolioForClose(null);
          }}
          onConfirm={handleConfirmClose}
        />
      )}
    </div>
  );
};

export default PortfoliosTab;
